import { useCallback, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useDefaultRatesQuery } from "@/modules/defaultRates/hooks/useDefaultRatesQuery";
import { RoleBasedProjectSettlementType } from "@/modules/projects/service/settlementType";
import { ReadDefaultRate } from "@/schemas/defaultRate";
import { Rates } from "@/schemas/commonFields/rate";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

interface UseDefaultRatesParams<TFormData extends Rates> {
  form: UseFormReturn<TFormData>;
  settlementType?: RoleBasedProjectSettlementType | "";
  validFrom?: string;
  enabled?: boolean;
}

interface UseDefaultRatesReturn {
  applyDefaultRates: () => Promise<void>;
  defaultRatesValidFromDate: string | null;
  isLoading: boolean;
}

export function useDefaultRates<TFormData extends Rates>({
  form,
  settlementType,
  validFrom,
  enabled = Boolean(settlementType),
}: UseDefaultRatesParams<TFormData>): UseDefaultRatesReturn {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [defaultRatesValidFromDate, setDefaultRatesValidFromDate] = useState<
    string | null
  >(null);

  const { data: defaultRates, isLoading: defaultRatesLoading } =
    useDefaultRatesQuery({
      enabled,
      params: {
        settlementType: settlementType as RoleBasedProjectSettlementType,
        validFrom,
      },
    });

  const setDefaultRateValues = useCallback(
    (defaultRateValues: ReadDefaultRate) => {
      if (!defaultRateValues) {
        return;
      }

      // Using type assertion to work around complex generic constraints
      const setValue = (field: string, value: string) => {
        (
          form.setValue as (
            field: string,
            value: string,
            opts: { shouldDirty: boolean },
          ) => void
        )(field, value, { shouldDirty: true });
      };

      setValue("rate_student_net", defaultRateValues.rate_student_net);
      setValue("rate_student_tax", defaultRateValues.rate_student_tax);
      setValue("rate_kch_net", defaultRateValues.rate_kch_net);
      setValue("rate_kch_tax", defaultRateValues.rate_kch_tax);
      setValue("rate_zus_net", defaultRateValues.rate_zus_net);
      setValue("rate_zus_tax", defaultRateValues.rate_zus_tax);
      setValue("rate_zus_sick_net", defaultRateValues.rate_zus_sick_net);
      setValue("rate_zus_sick_tax", defaultRateValues.rate_zus_sick_tax);
      setValue("rate_gross", defaultRateValues.rate_gross);
    },
    [form],
  );

  const applyDefaultRates = useCallback(async () => {
    const defaultRate = defaultRates?.items[0];
    if (settlementType && defaultRate) {
      setDefaultRatesValidFromDate(defaultRate.valid_from);
      setDefaultRateValues(defaultRate);
      toast({
        title: t("projectRates.defaults.success.apply"),
        variant: "default",
      });
    } else if (settlementType && !defaultRate) {
      toast({
        title: t("projectRates.defaults.errors.notFound"),
        variant: "destructive",
      });
    }
  }, [settlementType, defaultRates, setDefaultRateValues, toast, t]);

  return {
    applyDefaultRates,
    defaultRatesValidFromDate,
    isLoading: defaultRatesLoading,
  };
}
